import { captureException } from '@common/sentry';
import { ProphecyWSErrorTags, SentryTags } from '@common/sentry/SentryTags';
import { ProphecyError } from '@prophecy/utils/error';

import type { GroupAs } from '@common/sentry/groups';

export const NOT_CONNECTED = 'Websocket is not connected';

/** One socket being down is one fault, however many callers trip over it while it is down. */
const NOT_CONNECTED_GROUP: GroupAs = {
  title: NOT_CONNECTED,
  fingerprint: ['websocket-not-connected']
};

/**
 * Names the caller that found the socket down without putting it in the message. These carry no stack
 * - `ProphecyError` clears its own, and putting a JS one back would surface it in a toast - so the
 * message is the whole grouping key, and naming the caller there would file `md:send` and `md:cancel`
 * as unrelated issues. On a tag it is a breakdown inside one.
 *
 * A socket that never opened is left to the failed handshake already reported against it, since every
 * request queued behind one would only echo that. Those keep `report` set, so they still reach Sentry
 * through `onunhandledrejection` if nobody catches them, under the same single issue.
 *
 * Lives in its own module because the three websocket modules each spawn a ComlinkWorker at import
 * time; sharing this through any of them would start a second worker in the others.
 */
export function notConnectedError(source: string, everOpened: boolean) {
  const error = new ProphecyError(NOT_CONNECTED);
  if (!everOpened) return error;

  captureException({
    exception: error,
    errorTags: { [SentryTags.ProphecyErrorWSType]: ProphecyWSErrorTags.NotConnected },
    captureContext: { tags: { [SentryTags.WSNotConnectedSource]: source } },
    groupAs: NOT_CONNECTED_GROUP
  });

  // cleared only once captureException has seen it, since that drops anything already opted out
  error.report = false;
  return error;
}
